import React, { useEffect, useState } from "react";
import {
  Box,
  CardContent,
  Typography,
  LinearProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Avatar,
  Chip,
  Alert
} from "@mui/material";
import BentoCard from "components/bento/BentoCard";
import {
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Schedule as PendingIcon,
  Send as SendIcon
} from "@mui/icons-material";
import { getAllCustomerGroups } from "../../api/customerGroup";

const MessageProgress = ({ progress, customers }) => {
  const { sent, total, results } = progress;
  const [customerGroups, setCustomerGroups] = useState([]);

  useEffect(() => {
    getAllCustomerGroups().then((res) => {
      if (res.success && Array.isArray(res.data)) setCustomerGroups(res.data);
    });
  }, []);

  // Calculate progress percentage
  const progressPercentage = total > 0 ? Math.round((sent / total) * 100) : 0;

  // Get customer info by ID
  const getCustomerById = (customerId) => {
    return customers.find(c => c.id === customerId) || { name: 'Unknown', id: customerId };
  };

  // Human-readable group name for a WhatsApp group id (falls back to the raw id).
  const getGroupName = (groupId) => {
    if (!groupId) return groupId;
    return customerGroups.find(g => g.id === groupId)?.name || groupId;
  };

  // Get status color and icon
  const getStatusDisplay = (status) => {
    switch (status) {
      case 'success':
        return {
          icon: <CheckIcon sx={{ color: 'success.main' }} />,
          color: 'success',
          label: 'Sent'
        };
      case 'error':
        return {
          icon: <ErrorIcon sx={{ color: 'error.main' }} />,
          color: 'error',
          label: 'Failed'
        };
      case 'pending':
        return {
          icon: <PendingIcon sx={{ color: 'warning.main' }} />,
          color: 'warning',
          label: 'Pending'
        };
      default:
        return {
          icon: <SendIcon sx={{ color: 'info.main' }} />,
          color: 'info',
          label: 'Sending'
        };
    }
  };

  const successCount = results.filter(r => r.status === 'success').length;
  const errorCount = results.filter(r => r.status === 'error').length;
  const pendingCount = total - sent;

  return (
    <BentoCard bodySx={{ p: 0 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <SendIcon />
          Sending Progress
        </Typography>

        {/* Progress Bar */}
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography variant="body2" color="textSecondary">
              Progress
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {sent}/{total} ({progressPercentage}%)
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPercentage}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {/* Status Summary */}
        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          {successCount > 0 && (
            <Chip
              icon={<CheckIcon />}
              label={`${successCount} Sent`}
              color="success"
              size="small"
            />
          )}
          {errorCount > 0 && (
            <Chip
              icon={<ErrorIcon />}
              label={`${errorCount} Failed`}
              color="error"
              size="small"
            />
          )}
          {pendingCount > 0 && (
            <Chip
              icon={<PendingIcon />}
              label={`${pendingCount} Pending`}
              color="warning"
              size="small"
            />
          )}
        </Box>

        {/* Detailed Results */}
        {results.length > 0 && (
          <Box>
            <Typography variant="subtitle2" gutterBottom>
              Detailed Results
            </Typography>
            <Box sx={{ maxHeight: 300, overflow: 'auto' }}>
              <List dense>
                {results.map((result, index) => {
                  const customer = getCustomerById(result.customerId);
                  const statusDisplay = getStatusDisplay(result.status);
                  
                  return (
                    <ListItem key={index} sx={{ px: 0 }}>
                      <ListItemIcon sx={{ minWidth: 36 }}>
                        {statusDisplay.icon}
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                            <Avatar sx={{ width: 24, height: 24, fontSize: '0.75rem' }}>
                              {customer.name.charAt(0)}
                            </Avatar>
                            <Typography variant="body2">
                              {customer.name}
                            </Typography>
                            <Chip
                              label={statusDisplay.label}
                              size="small"
                              color={statusDisplay.color}
                              variant="outlined"
                            />
                            {result.groupId && (
                              <Chip
                                label={`Group: ${getGroupName(result.groupId)}`}
                                size="small"
                                color="info"
                                variant="outlined"
                                sx={{ fontSize: '0.6rem' }}
                              />
                            )}
                          </Box>
                        }
                        secondary={
                          result.error ? (
                            <Typography variant="caption" color="error">
                              {result.error}
                            </Typography>
                          ) : result.messageId ? (
                            <Typography variant="caption" color="textSecondary">
                              Message ID: {result.messageId}
                            </Typography>
                          ) : null
                        }
                      />
                    </ListItem>
                  );
                })}
              </List>
            </Box>
          </Box>
        )}

        {/* Final Status Alert */}
        {sent === total && total > 0 && (
          <Alert
            severity={errorCount === 0 ? 'success' : errorCount === total ? 'error' : 'warning'}
            sx={{ mt: 2 }}
          >
            {errorCount === 0
              ? `All ${total} messages sent successfully!`
              : errorCount === total
              ? `All ${total} messages failed to send.`
              : `${successCount} messages sent successfully, ${errorCount} failed.`
            }
          </Alert>
        )}
      </CardContent>
    </BentoCard>
  );
};

export default MessageProgress;